import { getCategory } from '@redux/category';
import * as ExperimentActions from '@redux/experiments';
import { lpSearchBarSelector } from '@redux/experiments/selectors/lp-search-bar';
import { requestQuoteCtaChangeSelector } from '@redux/experiments/selectors/request-quote-cta-change';
import { sortRelevanceModelSelector } from '@redux/experiments/selectors/sort-relevance-model';
import { getMember } from '@redux/index';
import {
	getCity,
	getIpAddress,
	getStateCode,
	updateLocation,
} from '@redux/location';
import { getConversations } from '@redux/messagedVendors/thunks';
import { getAllStateSlugs } from '@redux/states';
import { getVendorProfilesForRequest, getVendorTotal } from '@redux/vendors';
import { fetchWedding, getWeddingCity, getWeddingState } from '@redux/wedding';
import { experiments } from '@settings';
import { PageContainer } from '@tkww/orion-web-ds-page-layout';
import { GridColumn, GridRow } from '@tkww/orion-web-ds-structure';
import { Logger } from '@utils/logger/logger';
import { CookieStorage } from 'cookie-storage';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { MARKETPLACE_USER_LOCATION } from '../../../constants/cookies';
import { XO_SESSION_TOKEN } from '../../../constants/membership';
import {
	buildLocalBusinessItemListScript,
	getLandingLeadImage,
} from '../../../helpers/linkedData/localBusiness';
import { composeCanonicalUrlForPath } from '../../../helpers/seo';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import FooterLinks from '../../components/shared/FooterLinks';
import MetaContent from '../../components/shared/HelmetHeader/HelmetHeader';
import PhotoCredit from '../../components/shared/PhotoCredit';
import SearchWidget from '../../components/shared/SearchWidget';
import TextContent from '../../components/shared/TextContent';
import { AnalyticsContext } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { RFQModalContextProvider } from '../../contexts/RFQModalContext/RFQModalContext';
import { WeddingsContextProvider } from '../../contexts/WeddingsContext/WeddingsContext';
import ClientStyles from '../../styles.scss';
import { hasUniqueHeader } from '../../utils/categoryContentHelper';
import {
	CATEGORY_LANDING_PAGE,
	CATEGORY_LEVEL,
	CLICK_THROUGH_TO_VENDOR_CATEGORY,
	FOOTER,
	categoryViewed,
} from '../../utils/tracking';
import { CITIES, STATES } from '../../utils/urlHelpers';
import { Articles } from './Articles';
import { articles } from './Articles/config';
import Recommendations, {
	MAX_VENDOR_PROFILES,
	MIN_VISIBLE_VENDOR_PROFILES,
} from './Recommendations';
import { CATEGORY_FOOTER_CITIES } from './constants/categoryFooterCities';
import Styles from './styles.scss';
import { textContentInfo } from './textContentConfig';

export class Category extends Component {
	static propTypes = {
		allStates: PropTypes.arrayOf(
			PropTypes.shape({
				name: PropTypes.string.isRequired,
				slug: PropTypes.string.isRequired,
			}),
		).isRequired,
		category: PropTypes.shape({
			code: PropTypes.string.isRequired,
			id: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
			singular: PropTypes.shape({
				term: PropTypes.string.isRequired,
			}).isRequired,
			plural: PropTypes.shape({
				slug: PropTypes.string.isRequired,
			}).isRequired,
		}).isRequired,
		fetchWedding: PropTypes.func.isRequired,
		getConversations: PropTypes.func.isRequired,
		history: PropTypes.shape({
			location: PropTypes.shape({
				pathname: PropTypes.string.isRequired,
			}).isRequired,
		}).isRequired,
		lpSearchBarAssignment: PropTypes.oneOf(['control', 'variant1']),
		member: PropTypes.shape({
			id: PropTypes.string.isRequired,
		}),
		reportServerSideExperiment: PropTypes.func.isRequired,
		requestQuoteCtaChangeAssignment: PropTypes.oneOf([
			'control',
			'variant1',
			'variant2',
		]),
		sortRelevanceModelAssignment: PropTypes.string,
		updateLocation: PropTypes.func.isRequired,
		vendorTotal: PropTypes.number.isRequired,
		vendorProfiles: PropTypes.arrayOf(PropTypes.object).isRequired,
		weddingCity: PropTypes.string,
		weddingStateCode: PropTypes.string,
	};

	static defaultProps = {
		member: {
			id: '',
		},
		sortRelevanceModelAssignment: null,
		requestQuoteCtaChangeAssignment: null,
		lpSearchBarAssignment: null,
		weddingCity: null,
		weddingStateCode: null,
		vendorProfiles: [],
	};

	state = {
		memId: this.props.member ? this.props.member.id : null,
		isSearchByVendor: false,
	};

	componentDidMount() {
		const cookieStorage = new CookieStorage();
		const {
			member,
			getConversations,
			lpSearchBarAssignment,
			reportServerSideExperiment,
			requestQuoteCtaChangeAssignment,
			sortRelevanceModelAssignment,
		} = this.props;

		// Track category viewed
		categoryViewed(
			CATEGORY_LEVEL,
			this.context.track,
			this.props.category.code,
			{
				memberId: member ? member.id : '',
			},
		);

		if (sortRelevanceModelAssignment) {
			Logger.log({
				log_message: `Sort Relevance Model Assignment: ${sortRelevanceModelAssignment}`,
			});
			reportServerSideExperiment(experiments.sortRelevanceModel);
		}

		if (requestQuoteCtaChangeAssignment) {
			Logger.log({
				log_message: `Request Quote CTA Change Assignment: ${requestQuoteCtaChangeAssignment}`,
			});
			reportServerSideExperiment(experiments.requestQuoteCtaChange);
		}

		if (lpSearchBarAssignment) {
			Logger.log({
				log_message: `LP Search Bar Assignment: ${lpSearchBarAssignment}`,
			});
			reportServerSideExperiment(experiments.lpSearchBar);
		}

		if (member?.id) {
			const sessionToken = cookieStorage.getItem(XO_SESSION_TOKEN);

			if (sessionToken) {
				getConversations(member.id, sessionToken);
			}
		}

		// example way to set your local cookie
		// cookieStorage.setItem(MARKETPLACE_USER_LOCATION, JSON.stringify({ city: 'Las Vegas', stateCode: 'NV', market: '119' }), { path: '/' });
		// 1: Cookie, logged in or logged out, for Recs location
		if (cookieStorage.getItem(MARKETPLACE_USER_LOCATION)) {
			const cookieLocation = JSON.parse(
				cookieStorage.getItem(MARKETPLACE_USER_LOCATION).replace(/\+/g, ' '),
			);

			const locationHash = {
				city: cookieLocation.city,
				stateCode: cookieLocation.stateCode,
			};
			return this.props.updateLocation(locationHash, this.props.ip);
		}

		return null;
	}

	static getDerivedStateFromProps(props, state) {
		// prevents wedding fetch from happening before membership is defined
		if (
			(props.member ? props.member.id !== state.memId : null) &&
			props.member !== null
		) {
			return {
				memId: props.member.id,
			};
		}
		return null;
	}

	componentDidUpdate(prevProps, prevState) {
		const { ip, member, weddingCity, weddingStateCode, getConversations } =
			this.props;

		const cookieStorage = new CookieStorage();

		// Fetch conversations when member becomes available
		if (member?.id && !prevProps.member?.id) {
			const sessionToken = cookieStorage.getItem(XO_SESSION_TOKEN);

			if (sessionToken) {
				getConversations(member.id, sessionToken);
			}
		}

		const locationHash = { city: weddingCity, stateCode: weddingStateCode };
		// 2: Update Recs with wedding location if already logged in
		if (weddingCity !== prevProps.weddingCity) {
			return this.props.updateLocation(locationHash, ip);
			// 3: Fetch wedding if membership detected, but cookie will still take precedence in the state for Recs
		}
		if (
			prevState.memId === null &&
			member !== null &&
			!cookieStorage.getItem(MARKETPLACE_USER_LOCATION)
		) {
			return this.setLocationFromWedding();
		}
		// 4: Use backend loaded IP, which will default to New York, NY via getGeoFromIp function
		return null;
	}

	setLocationFromWedding() {
		const cookieStorage = new CookieStorage();

		return this.props.fetchWedding(
			this.props.member.id,
			cookieStorage.getItem(XO_SESSION_TOKEN),
		);
	}

	shouldRenderArticles() {
		const { code } = this.props.category;
		return code in articles;
	}

	shouldRenderTextContent() {
		return !!textContentInfo[this.props.category.code];
	}

	templateDescription() {
		return `Research and compare ${this.props.vendorTotal} ${this.props.category.plural.term} on The Knot. We offer reviews, quotes and details on vendors to ensure they match your needs to make your big day more special.`;
	}

	trackClickThroughToVendorCategory = () => {
		const {
			category: { code },
		} = this.props;

		this.context.track({
			event: CLICK_THROUGH_TO_VENDOR_CATEGORY,
			properties: {
				sourceContent: FOOTER,
				sourcePage: CATEGORY_LANDING_PAGE,
				vendorCategoryCode: code,
			},
		});
	};

	render() {
		const {
			code: categoryCode,
			id: categoryGuid,
			name: categoryName,
			plural: { slug: categorySlug },
			singular: { term: categoryTerm },
		} = this.props.category;
		const linkedDataScripts =
			this.props.vendorProfiles.length >= MIN_VISIBLE_VENDOR_PROFILES
				? buildLocalBusinessItemListScript({
						id: 'categoryLocalBusinessLdJson',
						vendors: this.props.vendorProfiles.slice(0, MAX_VENDOR_PROFILES),
						imageForVendor: getLandingLeadImage,
					})
				: null;

		return (
			<>
				<WeddingsContextProvider>
					<RFQModalContextProvider>
						<MetaContent
							canonical={composeCanonicalUrlForPath(categorySlug)}
							description={
								hasUniqueHeader(categoryCode)
									? hasUniqueHeader(categoryCode).description
									: this.templateDescription()
							}
							landingPage="category"
							{...(linkedDataScripts && { linkedDataScripts })}
							title={
								hasUniqueHeader(categoryCode)
									? hasUniqueHeader(categoryCode).headerName
									: this.props.category.name
							}
						/>

						<div className={Styles.topBreadcrumbsWrapper}>
							<Breadcrumbs categoryCode={categoryCode} isHeader />
						</div>

						<SearchWidget
							pageType={CATEGORY_LEVEL}
							categoryCode={categoryCode}
							categoryTerm={categoryTerm}
							categoryGuid={categoryGuid}
							headerName={
								hasUniqueHeader(categoryCode)
									? hasUniqueHeader(categoryCode).headerName
									: categoryName
							}
							subHeader={
								hasUniqueHeader(categoryCode)
									? hasUniqueHeader(categoryCode).subHeader
									: `Find the best ${categoryName} in your area.`
							}
						/>

						<PageContainer>
							<Recommendations categoryGuid={categoryGuid} />

							{this.shouldRenderArticles() && (
								<Articles category={{ categoryCode, categoryName }} />
							)}

							{this.shouldRenderTextContent() && (
								<TextContent
									header={`Finding a ${categoryTerm}`}
									textContent={textContentInfo[categoryCode]}
								/>
							)}

							<div className={ClientStyles.borderTop}>
								<FooterLinks
									category={{ categoryName, categorySlug }}
									links={this.props.allStates}
									trackHandler={this.trackClickThroughToVendorCategory}
									type={STATES}
								/>
							</div>

							<FooterLinks
								category={{ categoryName, categorySlug }}
								links={CATEGORY_FOOTER_CITIES}
								trackHandler={this.trackClickThroughToVendorCategory}
								type={CITIES}
							/>

							<PhotoCredit categoryCode={categoryCode} />

							<GridRow>
								<GridColumn xs="12">
									<div className={Styles.bottomBreadcrumbsWrapper}>
										<Breadcrumbs categoryCode={categoryCode} />
									</div>
								</GridColumn>
							</GridRow>
						</PageContainer>
					</RFQModalContextProvider>
				</WeddingsContextProvider>
			</>
		);
	}
}

export function mapStateToProps(state) {
	return {
		allStates: getAllStateSlugs(state),
		category: getCategory(state),
		ip: getIpAddress(state),
		lpSearchBarAssignment: lpSearchBarSelector(state),
		member: getMember(state),
		requestQuoteCtaChangeAssignment: requestQuoteCtaChangeSelector(state),
		sortRelevanceModelAssignment: sortRelevanceModelSelector(state),
		vendorTotal: getVendorTotal(state),
		vendorProfiles: getVendorProfilesForRequest(
			state,
			state.category.id,
			getCity(state),
			getStateCode(state),
		),
		weddingCity: getWeddingCity(state),
		weddingStateCode: getWeddingState(state),
	};
}

export const mapDispatchToProps = {
	fetchWedding,
	getConversations,
	reportServerSideExperiment: ExperimentActions.reportServerSideExperiment,
	updateLocation,
};

const enhance = compose(
	withRouter,
	connect(mapStateToProps, mapDispatchToProps),
);

Category.contextType = AnalyticsContext;

export default enhance(Category);
